import type { ErrorEvent, EventHint } from "@sentry/react";

// Host of the Unleash feature-flag service. Its SDK polls in the background and
// its fetch failures (network blips, ad-block) are third-party noise we cannot
// fix. We don't drop them (a real outage is worth knowing about) but group them
// under a single Sentry issue so they stop fragmenting the dashboard.
const configuredUnleashUrl = window?.env?.UNLEASH_URL?.trim();
const UNLEASH_HOST = new URL(
  configuredUnleashUrl || "https://unleash.alpaca.markets",
).hostname;

// Stable fingerprint so every Unleash poll failure collapses into one issue.
const UNLEASH_FINGERPRINT = "unleash-poll-failure";

const getExceptionValues = (event: ErrorEvent) => event.exception?.values ?? [];

const getEventText = (event: ErrorEvent): string => {
  const parts: (string | undefined)[] = [event.message, event.request?.url];

  for (const value of getExceptionValues(event)) {
    parts.push(value.type, value.value);
  }

  return parts.filter(Boolean).join(" ");
};

const getOriginalError = (hint?: EventHint): Error | undefined =>
  hint?.originalException instanceof Error ? hint.originalException : undefined;

const isUnleashError = (event: ErrorEvent, hint?: EventHint): boolean => {
  // FeatureFlagProvider captures Unleash SDK errors with this tag. The
  // underlying error is a bare "TypeError: Failed to fetch" with no host in its
  // message, so the tag is the only reliable signal.
  if (event.tags?.unleash === "error") {
    return true;
  }

  if (getEventText(event).includes(UNLEASH_HOST)) {
    return true;
  }

  return getOriginalError(hint)?.message.includes(UNLEASH_HOST) ?? false;
};

/**
 * Sentry `beforeSend` handler. Groups Unleash poll failures under a single
 * issue so they stop fragmenting the dashboard; every other event passes
 * through unchanged.
 *
 * Note: this intentionally does NOT filter API 401/403 responses. The API uses
 * those statuses in a range of situations, so blanket-suppressing them would
 * hide real problems.
 */
export const processSentryEvent = (
  event: ErrorEvent,
  hint?: EventHint,
): ErrorEvent => {
  if (isUnleashError(event, hint)) {
    event.fingerprint = [UNLEASH_FINGERPRINT];
    event.tags = { ...event.tags, component: "unleash" };
    event.level = "warning";
  }

  return event;
};
